import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import {
  DomSanitizer,
  Meta,
  SafeStyle,
  Title,
} from '@angular/platform-browser';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  BreadcrumbsService,
  IPathArray,
} from '../../shared/services/breadcrumbs.service';
import { PartsCatalogNavigationMenuComponent } from '../../shared/components/parts-catalog-navigation-menu/parts-catalog-navigation-menu.component';
import { QuickFiltersComponent } from '../../shared/components/quick-filters/quick-filters.component';
import { ShopNavigationBlockComponent } from '../../shared/components/shop-navigation-block/shop-navigation-block.component';
import { ShopFiltersComponent } from '../../shared/components/shop-filters/shop-filters.component';
import { ShopItemsComponent } from '../../shared/components/shop-items/shop-items.component';
import { MobilePartCatalogMenuComponent } from '../../shared/components/mobile-part-catalog-menu/mobile-part-catalog-menu.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import {
  Collection,
  CollectionBreadcrumb,
  FacetValueFilterInput,
  GetCollectionQuery,
  GetCollectionQueryVariables,
  Query,
  SavedVehicle,
  SearchProductsQuery,
  SearchProductsQueryVariables,
  SearchResponse,
  SearchResult,
  SearchResultSortParameter,
  SortOrder,
} from './../../graphql/generated/generated-types';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  scan,
  share,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { DataService } from '../../core/providers/data/data.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AssetPreviewPipe } from './../../shared/pipes/asset-preview.pipe';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { getRouteArrayParam } from '../../shared/utils/get-route-array-param';
import {
  EXTENDED_SEARCH_PRODUCTS,
  GET_COLLECTION,
} from './product-list.graphql';
import { SkeletonModule } from 'primeng/skeleton';
import { WatchQueryFetchPolicy } from '@apollo/client/core';
import { isEmpty, isEqual } from 'lodash-es';
import { Platform } from '@angular/cdk/platform';
import { StateService } from '../../core/state/old/state.service';
import { environment } from '../../../environments/environment';
import { EditableFilters, EditableManagedFilter } from './managed-filter.class';
import { PageDescriptionComponent } from '../../shared/components/page-description/page-description.component';
import { ScrollTopModule } from 'primeng/scrolltop';
import { assertValidSDL } from 'graphql/validation/validate';
import { ActiveService } from '../../core/providers/active/active.service';
import { VehicleTypeService } from '../../shared/services/vehicle-type.service';
import { SelectedModificationService } from '../../shared/services/selected-modification.service';

export interface ISingleValueFilterParams {
  [key: number]: string;
}

export interface IMultiValueFilterParams {
  [key: number]: string[];
}

export interface IShopParams {
  quickFilters?: string | null;
  filters?: string | null;
  search?: string | null;
  sort?: string | null;
  page?: string | null;
  inStock?: boolean | string | null;
  lf?: string | null;
}

@Component({
  selector: 'app-shop',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    PartsCatalogNavigationMenuComponent,
    QuickFiltersComponent,
    ShopNavigationBlockComponent,
    ShopFiltersComponent,
    ShopItemsComponent,
    MobilePartCatalogMenuComponent,
    PaginationComponent,
    ProgressSpinnerModule,
    SkeletonModule,
    PageDescriptionComponent,
    RouterLink,
    ScrollTopModule,
  ],
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  host: { ngSkipHydration: 'true' },
})
export class ShopComponent implements OnInit, OnDestroy {
  @ViewChild(ShopFiltersComponent, { static: false })
  shopFiltersComponent: ShopFiltersComponent;
  @ViewChild(ShopNavigationBlockComponent, { static: false })
  shopNavigationBlockComponent: ShopNavigationBlockComponent;
  productsQuery$!: Observable<SearchResponse>;

  products$!: Observable<SearchResult[]>;
  public products!: SearchResult[];

  totalResults$!: Observable<number>;
  public totalResults!: number;

  collection$!: Observable<Collection | undefined>;
  public collection!: Collection | undefined;

  collectionSlug$: Observable<string | null>;
  slugChanged: boolean = false;

  public filters: EditableFilters = EditableFilters.adapt({
    top: [],
    left: [],
  });

  public mutableResponseFilters: EditableFilters = EditableFilters.adapt({
    top: [],
    left: [],
  });

  public unfilteredTotalItems: number = 0;

  activeQuickFilters$!: Observable<IMultiValueFilterParams>;
  activeLeftFilters$!: Observable<IMultiValueFilterParams>;

  inStock$!: Observable<boolean>;

  searchTerm$!: Observable<string>;
  public previousSearchTerm: string | any;
  public searchTerm!: string;

  sort$!: Observable<SearchResultSortParameter>;
  public sort: SearchResultSortParameter;

  initialLoading$!: Observable<boolean>;
  public initialLoading: boolean = true;

  loading$!: Observable<any>;
  public loading!: boolean;

  breadcrumbs$!: Observable<CollectionBreadcrumb[]>;
  mastheadBackground$!: Observable<SafeStyle>;

  page$!: Observable<number>;
  private refresh = new BehaviorSubject<void>(undefined);
  public currentPage = 0;
  public perPage: number = 26;

  private triggerFetch$!: Observable<
    [
      Collection | undefined,
      IMultiValueFilterParams,
      IMultiValueFilterParams,
      SearchResultSortParameter,
      string,
      boolean,
      void
    ]
  >;
  private initialLeftFilters$: Observable<Query> = of({} as Query);

  private unsubscribe$ = new Subject<void>();

  private activeQuickFilters: IMultiValueFilterParams = {};
  private activeLeftFilters: IMultiValueFilterParams = {};

  public notFoundProducts: boolean = false;
  public productsSkeletonArray: { id: number }[] = [];

  public activeVehicle$: Observable<SavedVehicle | null>;
  public activeVehicle: SavedVehicle | null;
  constructor(
    private breadcrumbsService: BreadcrumbsService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private stateService: StateService,
    private sanitizer: DomSanitizer,
    private platform: Platform,
    private router: Router,
    private metaService: Meta,
    private titleService: Title,
    private activeService: ActiveService,
    public vehicleTypeService: VehicleTypeService,
    public selectedModificationService: SelectedModificationService
  ) {}

  ngOnInit(): void {
    for (let i = 0; i < 26; i++) {
      this.productsSkeletonArray.push({ id: i });
    }

    if (this.platform.isBrowser) {
      window.scrollTo({ top: 0 });
    }
    this.subsccribeToActiveVehicle();

    // COLLECTION
    this.createCollectionSlugObservable();
    this.createCollectionObservable();
    this.collection$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (collection: Collection | undefined) => {
        if (collection !== undefined) {
          this.collection = collection;
        }
        this.updateShopSeo();

        if (!collection || (this.collection && this.collection.populatable)) {
          // QUERY PARAMS

          this.createQuickFiltersQueryParamsObservable();
          this.subscribeToQuickFilterChanges();

          this.createLeftFiltersQueryParamsObservable();
          this.subscribeToLeftFilterChanges();

          this.createInStockParamObservable();

          this.createSearchQueryParamObservable();
          this.subscribeToSearchTerm();

          this.createSortQueryParamObservable();

          // BACKGROUND IMAGE
          // this.createMastHeadBackgroundObservable();

          // Breadcrumbs
          this.createBreadcrumbsObservable();
          this.subscribeToBreadCrumbsObservable();

          // PAGES
          this.createPageParamObservable();
          this.subscribeToPageParamChanges();

          // Create trigger for fetching
          this.createTriggerForFetchingObservable();

          // Create loading observable
          this.createLoadingObservable();

          // Create products query observable

          this.createProductsQueryObservable();

          // Create products query items observable and reset observable to merge both and scan
          this.createProductsResetObservable();

          // Create TotalResults observable
          this.createTotalResultsObservable();

          // Subscribe to products observable
          this.subscribeToProductsObservable();

          // Subscribe to total results observable:
          this.subscribeToTotalResultsObservable();
        }
      },
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private subsccribeToActiveVehicle() {

      this.activeVehicle$ = this.activeService.activeVehicle$?.pipe(
        takeUntil(this.unsubscribe$)
      );

      this.activeVehicle$?.subscribe({
        next : (response: SavedVehicle | null) => {
          if(response){
            this.activeVehicle = response;
          } else {
            this.activeVehicle = null;
          }
        }
      })
    

  }

  private createLoadingObservable() {
    this.loading$ = merge(this.triggerFetch$!.pipe(map(() => true)));
  }

  private createTriggerForFetchingObservable() {
    this.triggerFetch$! = combineLatest([
      this.collection$,
      this.activeQuickFilters$,
      this.activeLeftFilters$,
      this.sort$,
      this.searchTerm$,
      this.inStock$,
      this.refresh,
    ]);
  }

  private getInitialLeftFiltersOnly(
    activeQuickFilters: IMultiValueFilterParams,
    searchResultsLeftFilters: EditableManagedFilter[]
  ) {
    const quickFilters: { or: string[] }[] = Object.keys(
      activeQuickFilters
    ).map((id) => ({ or: activeQuickFilters[parseInt(id, 10)] }));
    // this.inititalLeftFiltersQuery$
    this.initialLeftFilters$ = combineLatest([
      this.collection$,
      this.searchTerm$,
      this.inStock$,
    ]).pipe(
      take(1),
      switchMap(([collection, term, inStock]) => {
        return this.dataService.query<
          SearchProductsQuery,
          SearchProductsQueryVariables
        >(
          EXTENDED_SEARCH_PRODUCTS,
          {
            input: {
              term: this.initialLoading ? '' : term,
              groupByProduct: true,
              facetValueFilters: quickFilters,
              collectionId: collection?.id,
              take: this.perPage,
              skip: this.currentPage * this.perPage,
              inStock: inStock ? inStock : undefined,
            },
          },
          'cache-and-network' as WatchQueryFetchPolicy
        );
      }),
      map((data: SearchProductsQuery) => data as Query),
    );

    this.initialLeftFilters$
      .pipe(takeUntil(this.unsubscribe$), take(1))
      .subscribe({
        next: (data) => {
          this.mutableResponseFilters.updateLeft([
            ...data.extendedSearch.filters.left,
          ]);
          this.filters.updateLeft([...this.mutableResponseFilters.left]);
          if (environment.websiteSettings.enableLeftFiltersCountChange) {
            this.filters.updateLeft(
              this.updateFilterValues(
                this.filters.left,
                searchResultsLeftFilters,
                true
              )
            );
          }

          this.initialLoading = false;
        },
      });
  }

  private createProductsQueryObservable() {
    this.productsQuery$ = this.triggerFetch$.pipe(
      switchMap(
        ([
          collection,
          activeQuickFilters,
          activeLeftFilters,
          sortParams,
          term,
          inStock,
          refresh,
        ]) => {
          if (!this.initialLoading) {
            if (
              !isEqual(this.activeLeftFilters, activeLeftFilters) ||
              !isEqual(this.activeQuickFilters, activeQuickFilters)
            ) {
              this.removePageRoute();
            }
          }

          if (!isEqual(this.activeQuickFilters, activeQuickFilters)) {
            if (this.shopFiltersComponent) {
              this.shopFiltersComponent.clearFiltersWithoutRoute();
              activeLeftFilters = {};
              this.removePageRoute(); // THIS TRIGGERS A SECOND FETCH TO API FIX LATER
            }
          }
          const quickFilters: { or: string[] }[] = Object.keys(
            activeQuickFilters
          ).map((id) => ({ or: activeQuickFilters[parseInt(id, 10)] }));
          const leftFilters: { or: string[] }[] = Object.keys(
            activeLeftFilters
          ).map((id) => ({ or: activeLeftFilters[parseInt(id, 10)] }));

          const graphqlFilterInput: FacetValueFilterInput[] = [];

          graphqlFilterInput.push(...quickFilters); // Edit so only push quickFilters objects
          graphqlFilterInput.push(...leftFilters);
          // The combined facetValueFilters array can now be used in GraphQL input
          return this.dataService
            .query<SearchProductsQuery, SearchProductsQueryVariables>(
              EXTENDED_SEARCH_PRODUCTS,
              {
                input: {
                  term,
                  groupByProduct: true,
                  collectionId: collection?.id,
                  facetValueFilters: graphqlFilterInput,
                  take: this.perPage,
                  skip: this.currentPage * this.perPage,
                  sort: sortParams,
                  inStock: inStock ? inStock : undefined,
                },
              },
              'no-cache' as WatchQueryFetchPolicy
            )
            .pipe(
              map((data: SearchProductsQuery) => data as Query),
              tap((data) => {
                if (
                  !data.extendedSearch.items ||
                  data.extendedSearch.items?.length == 0
                ) {
                  this.notFoundProducts = true;
                } else {
                  this.notFoundProducts = false;
                }
                if (data?.extendedSearch?.filters?.top) {
                  this.mutableResponseFilters.updateTop([
                    ...data?.extendedSearch?.filters?.top,
                  ]);
                }

                if (data?.extendedSearch?.filters?.left) {
                  this.mutableResponseFilters.updateLeft([
                    ...data?.extendedSearch.filters?.left,
                  ]);
                }
                this.totalResults = data.extendedSearch.totalItems;

                if (this.initialLoading) {
                  // FIRST LOAD
                  this.filters.updateTop([...this.mutableResponseFilters.top]);
                  if (!isEmpty(activeLeftFilters)) {
                    this.getInitialLeftFiltersOnly(
                      activeQuickFilters,
                      this.mutableResponseFilters.left
                    );
                  } else {
                    this.filters.updateLeft([
                      ...this.mutableResponseFilters.left,
                    ]);
                  }
                  this.unfilteredTotalItems = data.extendedSearch.totalItems;
                } else {
                  // OTHER LOADS
                  if (
                    isEqual(this.activeQuickFilters, activeQuickFilters) &&
                    !this.slugChanged &&
                    term == this.previousSearchTerm
                  ) {
                    // Top, slug and search does not change
                    if (environment.websiteSettings.enableLeftFiltersCountChange) {
                      this.filters.updateLeft(
                        this.updateFilterValues(
                          this.filters.left,
                          this.mutableResponseFilters.left
                        )
                      );
                    }
                  } else {
                    // TOPFILTERS CHANGED , UPDATE ALL :
                    this.filters.updateTop([
                      ...this.mutableResponseFilters.top,
                    ]);
                    this.filters.updateLeft([
                      ...this.mutableResponseFilters.left,
                    ]);
                  }
                }
                this.slugChanged = false;
                this.activeQuickFilters = activeQuickFilters;
                this.activeLeftFilters = activeLeftFilters;
                this.previousSearchTerm = term;
                if (isEmpty(activeLeftFilters)) {
                  this.initialLoading = false;
                }
              }),
              map((data: Query) => data.extendedSearch as SearchResponse)
            );
        }
      ),
      shareReplay(1),
      debounceTime(50),
      takeUntil(this.unsubscribe$)
    );
    this.loading$ = merge(
      this.triggerFetch$.pipe(map(() => true)),
      this.productsQuery$.pipe(map(() => false))
    );
  }

  private updateFilterValues(
    oldLeftilters: EditableManagedFilter[],
    newLeftFilters: EditableManagedFilter[],
    init: boolean = false
  ): EditableManagedFilter[] {
    oldLeftilters.forEach((filter: EditableManagedFilter, index: number) => {
      const lastFilterChangedId: string | null =
        this.route.snapshot.queryParamMap.get('lf');
      //was below in if && !init
      if (lastFilterChangedId === filter.id) {
        // Do nothing for last filter group - dont change counts
      } else {
        const newDataFilter = newLeftFilters.find(
          (newFilter: EditableManagedFilter) => newFilter.id === filter.id
        );

        if (newDataFilter) {
          filter.values.forEach(
            (value: EditableManagedFilter, valueIndex: number) => {
              const newValue = newDataFilter.values.find(
                (newValue: EditableManagedFilter) => newValue.id === value.id
              );
              if (newValue && newValue.id === value.id) {
                // Matching value found, update count
                oldLeftilters[index].values[valueIndex].count = newValue.count;
              } else {
                // No matching value found, set count to
                oldLeftilters[index].values[valueIndex].count = 0;
              }
            }
          );
        } else {
          // Filter does not exist in new data, set all counts to 0
          filter.values.forEach((value: any, valueIndex: number) => {
            oldLeftilters[index].values[valueIndex].count = 0;
          });
        }
      }
    });
    return [...oldLeftilters];
  }

  private createProductsResetObservable() {
    const items$ = (this.products$ = this.productsQuery$.pipe(
      map((data) => data.items)
    ));
    const reset$ = merge(
      this.collectionSlug$,
      this.activeQuickFilters$,
      this.activeLeftFilters$,
      this.sort$,
      this.searchTerm$,
      this.inStock$
    ).pipe(
      map(() => 'RESET'),
      skip(1),
      share()
    );
    this.products$ = merge(items$, reset$).pipe(
      scan<SearchResult[] | string, SearchResult[]>((acc, val) => {
        if (typeof val === 'string') {
          return [];
        } else {
          return val;
        }
      }, [] as SearchResult[])
    );
  }

  private createTotalResultsObservable() {
    this.totalResults$ = this.productsQuery$.pipe(
      map((data) => data.totalItems)
    );
  }

  private subscribeToProductsObservable() {
    this.products$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (products: SearchResult[]) => {
        this.products = products;
      },
    });
  }

  private subscribeToTotalResultsObservable() {
    this.totalResults$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (totalResults: number) => {
        this.totalResults = totalResults;
      },
      error: (error: HttpErrorResponse) => {},
      complete: () => {},
    });
  }

  private createCollectionSlugObservable() {
    this.collectionSlug$ = this.route.paramMap.pipe(
      map((pm) => pm.get('slug')),
      distinctUntilChanged(),
      tap((slug) => {
        this.stateService.setState('lastCollectionSlug', slug || null);
        if (!this.initialLoading) {
          this.slugChanged = true;
          this.currentPage = 0;
        }
      }),
      shareReplay(1)
    );
  }

  private createQuickFiltersQueryParamsObservable() {
    this.activeQuickFilters$ = this.route.queryParamMap.pipe(
      map((pm) => {
        const queryParamsArray = getRouteArrayParam(pm, 'quickFilters');
        // Declare queryParamsObject with a type including an index signature for number keys
        const queryParamsObject: IMultiValueFilterParams = {};

        queryParamsArray.forEach((item) => {
          const [key, valuesString] = item.split(':');
          if (valuesString !== undefined) {
            const valuesArray = valuesString.split(';'); // Split the values string into an array of strings
            queryParamsObject[parseInt(key, 10)] = valuesArray;
          } else {
            console.error(`Values string is undefined for key ${key}`);
          }
        });
        return queryParamsObject;
      }),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      tap(() => {
        if (!this.initialLoading) {
          this.currentPage = 0;
        }
      }),
      shareReplay(1)
    );
  }

  private subscribeToQuickFilterChanges() {
    this.activeQuickFilters$.subscribe((value) => {});
  }

  private createLeftFiltersQueryParamsObservable() {
    this.activeLeftFilters$ = this.route.queryParamMap.pipe(
      map((pm) => {
        const queryParamsArray = getRouteArrayParam(pm, 'filters');

        // Declare queryParamsObject with a type including an index signature for number keys
        const queryParamsObject: IMultiValueFilterParams = {};

        queryParamsArray.forEach((item) => {
          const [key, valuesString] = item.split(':');
          if (valuesString !== undefined) {
            const valuesArray = valuesString.split(';'); // Split the values string into an array of strings
            queryParamsObject[parseInt(key, 10)] = valuesArray;
          } else {
            console.error(`Values string is undefined for key ${key}`);
          }
        });
        return queryParamsObject;
      }),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      tap(() => {
        if (!this.initialLoading) {
          this.currentPage = 0;
        }
      }),
      shareReplay(1)
    );
  }

  private subscribeToLeftFilterChanges() {
    this.activeLeftFilters$.subscribe((value) => {});
  }

  private createPageParamObservable() {
    this.page$ = this.route.queryParamMap.pipe(
      map((pm) => parseInt(pm.get('page') || '0', 10)),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  private subscribeToPageParamChanges() {
    this.page$.subscribe((value: number) => {
      this.currentPage = value;
      this.refresh.next();
    });
  }

  private removePageRoute() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: null } as IShopParams,
      queryParamsHandling: 'merge',
      state: {
        noScroll: true,
      },
    });
  }

  private createInStockParamObservable() {
    this.inStock$ = this.route.queryParamMap.pipe(
      map((pm) => pm.has('inStock') || false),
      distinctUntilChanged(),
      tap(() => {
        if (!this.initialLoading) {
          this.currentPage = 0;
        }
      }),
      shareReplay(1)
    );
  }

  private createSearchQueryParamObservable() {
    this.searchTerm$ = this.route.queryParamMap.pipe(
      map((pm) => pm.get('search') || ''),
      distinctUntilChanged(),
      tap((pm) => {
        if (!this.initialLoading) {
          this.currentPage = 0;
        }
        if (!pm || pm === '') {
          this.shopNavigationBlockComponent?.clearSearchControl();
        }
      }),
      shareReplay(1)
    );
  }

  private createSortQueryParamObservable() {
    this.sort$ = this.route.queryParamMap.pipe(
      map((pm) => {
        const queryParamsArray = getRouteArrayParam(pm, 'sort');

        // Declare queryParamsObject with a type including an index signature for number keys
        const queryParamsObject: SearchResultSortParameter =
          queryParamsArray.reduce((acc, current) => {
            const [key, value] = current.split(':');
            return { ...acc, [key]: value.toUpperCase() as SortOrder };
          }, {});

        return queryParamsObject;
      }),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      shareReplay(1)
    );
  }

  private createCollectionObservable() {
    this.collection$ = this.collectionSlug$.pipe(
      switchMap((slug) => {
        if (slug) {
          return this.dataService
            .query<GetCollectionQuery, GetCollectionQueryVariables>(
              GET_COLLECTION,
              {
                slug,
              }
            )
            .pipe(map((data) => data.collection as Collection),
            map((collection: Collection) => {
              if (environment.websiteSettings.hideEmptyCatalogCategories) {
              return {
                ...collection,
                children: collection.children?.filter(child => child.productsCount !== 0) ?? [],
              };
              }

              return collection;
      
            })
          );
        } else {
          this.updateShopSeo();
          return of(undefined);
        }
      }),
      tap(() => {
        if (!this.initialLoading) {
          this.currentPage = 0;
        }
      }),
      shareReplay(1)
    );
  }

  private subscribeToCollectionObservable() {
    this.collection$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (collection: Collection | undefined) => {
        if (collection !== undefined) {
          this.collection = collection;
        }
        this.updateShopSeo();
      },
    });
  }

  private updateShopSeo() {
    if (this.collection) {
      const assetPreviewPipe = new AssetPreviewPipe();
      const collectionName = this.collection?.name;
      const collectionSlug = this.collection?.slug;
      const collectionDescription =
        this.collection?.description ||
        'Explore our wide range of premium auto parts at Fastparts.';
      const collectionKeywords = `shop, auto parts, vehicle parts, Fastparts, car parts, browse products, ${collectionSlug}, ${collectionName}`;
      const collectionTitle = `${collectionName} | Fastparts`;
      const featuredAsset = assetPreviewPipe.transform(
        this.collection?.featuredAsset || undefined,
        1000,
        300
      );

      this.metaService.updateTag({
        name: 'description',
        content: collectionDescription,
      });
      this.metaService.updateTag({
        name: 'keywords',
        content: collectionKeywords,
      });
      this.titleService.setTitle(collectionTitle);

      this.metaService.updateTag({
        property: 'og:description',
        content: collectionDescription,
      });
      this.metaService.updateTag({
        name: 'twitter:description',
        content: collectionDescription,
      });
      this.metaService.updateTag({
        property: 'og:title',
        content: collectionTitle,
      });
      this.metaService.updateTag({
        name: 'twitter:title',
        content: collectionTitle,
      });

      this.metaService.updateTag({
        name: 'og:image',
        content: 'https://dev.shop.fastparts.is/media/hero-lg-YN3VJ6A7.png',
      });
      this.metaService.updateTag({
        name: 'twitter:image',
        content: 'https://dev.shop.fastparts.is/media/hero-lg-YN3VJ6A7.png',
      });
    } else {
      this.metaService.updateTag({
        name: 'description',
        content:
          'Explore our wide range of premium auto parts at Fastparts. Browse by category, vehicle type, or specific needs to find the perfect products for your vehicle. Enjoy a seamless shopping experience with detailed descriptions and high-quality images.',
      });
      this.metaService.updateTag({
        name: 'keywords',
        content:
          'shop, auto parts, vehicle parts, Fastparts, car parts, browse products',
      });
      this.titleService.setTitle('Shop | Explore Auto Parts at Fastparts');

      this.metaService.updateTag({
        property: 'og:description',
        content:
          'Explore our wide range of premium auto parts at Fastparts. Browse by category, vehicle type, or specific needs to find the perfect products for your vehicle. Enjoy a seamless shopping experience with detailed descriptions and high-quality images.',
      });
      this.metaService.updateTag({
        name: 'twitter:description',
        content:
          'Explore our wide range of premium auto parts at Fastparts. Browse by category, vehicle type, or specific needs to find the perfect products for your vehicle. Enjoy a seamless shopping experience with detailed descriptions and high-quality images.',
      });
      this.metaService.updateTag({
        property: 'og:title',
        content: 'Shop | Explore Auto Parts at Fastparts',
      });
      this.metaService.updateTag({
        name: 'twitter:title',
        content: 'Shop | Explore Auto Parts at Fastparts',
      });

      this.metaService.updateTag({
        name: 'og:image',
        content: 'https://dev.shop.fastparts.is/media/hero-lg-YN3VJ6A7.png',
      });
      this.metaService.updateTag({
        name: 'twitter:image',
        content: 'https://dev.shop.fastparts.is/media/hero-lg-YN3VJ6A7.png',
      });
    }
  }
  private createMastHeadBackgroundObservable() {
    const assetPreviewPipe = new AssetPreviewPipe();
    this.mastheadBackground$ = this.collection$.pipe(
      map(
        (c) =>
          'url(' +
          assetPreviewPipe.transform(c?.featuredAsset || undefined, 1000, 300) +
          ')'
      ),
      map((style) => this.sanitizer.bypassSecurityTrustStyle(style))
    );
  }

  private createBreadcrumbsObservable() {
    return (this.breadcrumbs$ = this.collection$.pipe(
      map((collection) => {
        if (collection) {
          return collection.breadcrumbs as CollectionBreadcrumb[];
        } else {
          return [
            {
              id: '',
              name: 'Home',
            },
            {
              id: '',
              name: 'Search',
            },
          ] as CollectionBreadcrumb[];
        }
      })
    ));
  }

  private subscribeToBreadCrumbsObservable() {
    combineLatest([this.createBreadcrumbsObservable(), this.searchTerm$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([breadcrumbs, searchTerm]: [
          CollectionBreadcrumb[] | any[],
          string
        ]) => {
          let formattedBreadcrumbs: IPathArray[] | any = [];
          if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
            if ('slug' in breadcrumbs[0]) {
              // Case: Collection

              const mappedBreadcrumbs = breadcrumbs
                .map((breadcrumb, index) => ({
                  label: breadcrumb.name,
                  routerLink: `/${this.vehicleTypeService.currentVehicleType}/shop/category/${breadcrumb.slug}`,
                  queryParams: {
                    modificationId: this.selectedModificationService?.selectedModificationId,
                  },
                  arrow: index !== breadcrumbs.length - 1,
                }))
                .filter(
                  (breadcrumb, index) =>
                    !(index === 0 && breadcrumb.label === '__root_collection__')
                );

              formattedBreadcrumbs = [
                {
                  label: 'Parts catalog',
                  routerLink: `/${this.vehicleTypeService?.currentVehicleType}/parts-catalog`,
                  queryParams: {modificationId: this.selectedModificationService?.selectedModificationId},
                  arrow: breadcrumbs.length > 0, // Set arrow based on whether there are other breadcrumbs
                },
                ...mappedBreadcrumbs, // Combine with the mapped breadcrumbs
              ];
            } else {
              // Case: Search Term
              formattedBreadcrumbs = breadcrumbs
                .map((breadcrumb, index) => ({
                  label: breadcrumb.name,
                  routerLink: `/${this.vehicleTypeService.currentVehicleType}/shop`,
                  queryParams: {
                    modificationId: this.selectedModificationService?.selectedModificationId,
                  },
                  arrow: true,
                }))
                .filter(
                  (breadcrumb, index) =>
                    !(index === 0 && breadcrumb.label === 'Home')
                );

              if (searchTerm) {
                formattedBreadcrumbs.push({
                  label: searchTerm,
                  routerLink: `/${this.vehicleTypeService.currentVehicleType}/shop/search`,
                  queryParams: { search: searchTerm, modificationId: this.selectedModificationService?.selectedModificationId },
                  arrow: false,
                });
              }
            }
          } else {
            // Case: Unknown Format
            this.breadcrumbsService.setBreadcrumbs([
              {
                label: 'Unknown',
                routerLink: '/',
                arrow: false,
              },
            ]);
          }
          this.breadcrumbsService.setBreadcrumbs(formattedBreadcrumbs);
        },
      });
  }

  private subscribeToSearchTerm() {
    this.searchTerm$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (searchTerm: string) => {
        this.searchTerm = searchTerm;
      },
    });
  }
}
